/**
 * Step-Up Verification Banner
 *
 * Displays a user-facing explanation when additional verification steps are
 * required. Typically shown at the top of the step-up page to guide the
 * applicant through the extra requirements.
 *
 * @module components/StepUpBanner
 */

import { useTranslation } from 'react-i18next';

export interface StepUpBannerProps {
  /** List of step-up actions required. */
  actions: string[];
  /** Optional reason string explaining why step-up was triggered. */
  reason?: string;
}

export function StepUpBanner({ actions, reason }: StepUpBannerProps) {
  const { t } = useTranslation('portal');
  if (actions.length === 0) return null;

  // Lookup translated label/description for an action key, falling back
  // to the key itself for unknown actions.
  const labelFor = (action: string) =>
    t(`stepUpBanner.actions.${action}.label`, { defaultValue: action });
  const descriptionFor = (action: string) =>
    t(`stepUpBanner.actions.${action}.description`, { defaultValue: '' });

  // Filter to actions that require user interaction
  const userActions = actions.filter(
    (a) => a !== 'manual_review' && a !== 'enhanced_screening',
  );
  const backgroundActions = actions.filter(
    (a) => a === 'manual_review' || a === 'enhanced_screening',
  );

  return (
    <div className="rounded-lg border border-warning/30 bg-warning-tint p-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <svg
          className="mt-0.5 h-6 w-6 shrink-0 text-warning"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
          />
        </svg>
        <div>
          <h3 className="font-display text-lg font-medium tracking-tight text-ink">
            {t('stepUpBanner.title')}
          </h3>
          <p className="mt-1.5 text-sm leading-relaxed text-ink-muted">
            {t('stepUpBanner.description')}
            {reason && (
              <span className="mt-1.5 block text-xs text-ink-muted">
                {t('stepUpBanner.reason', { reason })}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* User-action requirements */}
      {userActions.length > 0 && (
        <div className="mt-5 space-y-3">
          <p className="font-mono text-xs uppercase tracking-[0.1em] text-ink-muted">
            {t('stepUpBanner.completeFollowing')}
          </p>
          <ul className="space-y-2">
            {userActions.map((action) => (
              <li
                key={action}
                className="flex items-start gap-2 rounded-md border border-warning/20 bg-white px-4 py-3"
              >
                <svg
                  className="mt-0.5 h-4 w-4 shrink-0 text-warning"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <span className="text-sm font-medium text-ink">
                    {labelFor(action)}
                  </span>
                  <p className="mt-0.5 text-xs text-ink-muted">
                    {descriptionFor(action)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Background actions (informational) */}
      {backgroundActions.length > 0 && (
        <div className="mt-5 rounded-md bg-warning/10 px-4 py-3">
          <p className="text-xs leading-relaxed text-ink-muted">
            <span className="font-medium">{t('stepUpBanner.noteLabel')}</span>{' '}
            {backgroundActions.map((a) => labelFor(a)).join(t('stepUpBanner.joinSeparator'))}{' '}
            {t('stepUpBanner.backgroundNoteSuffix')}
          </p>
        </div>
      )}
    </div>
  );
}
